import * as React from 'react';
import { useState, useEffect } from 'react';
import { Field } from 'redux-form';
import {
  FormWrapper,
  Content,
  FieldWrapper,
  FieldDate,
  Footer,
  More,
  Error,
  TooltipWrapper
} from './styled';
import { useToggle } from './hooks/useToggle';
import { RenderInput } from '@app/src/views/CipReport/filter/components/RenderInput';
import { RenderSelect } from '@app/src/views/CipReport/filter/components/RenderSelect';
import { RenderDatePicker } from '@app/src/views/CipReport/filter/components/RenderDatePicker';
import { fetchOperator } from '@app/src/views/CipReport/filter/state/operator/actions';
import { fetchPaymentType } from '@app/src/views/CipReport/filter/state/paymentType/actions';
import { fetchValidationReportToken } from '@app/src/views/CipReport/filter/state/validationReportToken/actions';
import { registerFilterValues } from '@app/src/views/CipReport/report/state/filterValues/actions';
import * as selectFilterValues from '@app/src/views/CipReport/report/state/filterValues/selectors';
import { normalizeAlphanumeric } from '@app/src/utils/normalizers';
import { validateSelectedDateRange } from '@app/src/views/CipReport/filter/state/validationDateRange/actions';
import { Button } from '@app/src/components/Button';
import { Tooltip } from '@app/src/components/Tooltip';
import { minLength } from '@app/src/utils/validations';
import { CIP_STATES } from './states';
import { ModalContainer } from '@app/src/utils/ModalLogout';
import { openModal } from '@app/src/commons/modal/state/modals/actions';
import { decodeAccessToken } from '@app/src/helpers/decodeAccessToken';
import { getSettings } from '@app/src/configuration/config.service';

const minLength8 = minLength(8);

interface Props {
  handleSubmit: Function;
  operators: Array<object>;
  paymentTypes: Array<object>;
  items: Array<object>;
  filter: object;
  cip?: string;
  documentNumber?: string;
  isFetchingDetails: boolean;
  detailErrorMessage?: string;
  startDateErrorMessage?: string;
  endDateErrorMessage?: string;
  dispatch: Function;
  reset: any;
  message: string;
  sessionRol: boolean;
}

export const Form: React.FC<Props> = ({
  handleSubmit,
  operators,
  paymentTypes,
  items,
  filter,
  cip,
  documentNumber,
  isFetchingDetails,
  detailErrorMessage,
  startDateErrorMessage,
  endDateErrorMessage,
  dispatch,
  reset,
  message,
  sessionRol
}) => {
  const toggle = useToggle();

  const isAdmin: boolean = decodeAccessToken('isAdmin');
  const dateCreated: string = decodeAccessToken('dateCreated');
  
  useEffect(() => {

    const loadData = async () => {
      if (isAdmin) {
        await dispatch(fetchOperator(getSettings().API_USER_ALIAS));
      }
      await dispatch(fetchPaymentType(getSettings().API_PAYMENT_TYPE));
      dispatch(registerFilterValues({
        ...filter,
        userId: selectFilterValues.getIdUser(),
        dateStart: selectFilterValues.getStartDate(),
        dateEnd: selectFilterValues.getEndDate()
      }));
    };

    loadData();

  }, []);

  const [ lockCip, setLockCip ] = useState(null);
  const [ lockDocument, setLockDocument ] = useState(null);
  const [ showModal, setShowModal ] = useState(false)

  const handleChangeInput = () => {
    !!cip ? setLockDocument(true) : setLockDocument(false);
    !!documentNumber ? setLockCip(true) : setLockCip(false);
  };

  useEffect(() => {
    handleChangeInput();
  });

  const handleChangeDatePicker = (date: string) => (position: string) => {
    dispatch(validateSelectedDateRange({
      selected: date,
      position
    }));
  };

  const handleClickDownload = () => {
    const reportToken = sessionStorage.getItem('reportToken');
    if (!reportToken) {
      dispatch(openModal('CLOSE_SESSION_MODAL'));
      setShowModal(true)
      return
    }
    dispatch(fetchValidationReportToken(getSettings().API_VALIDATION_REPORT_TOKEN, filter, true));
  };

  const isDisabledButton: boolean = items.length === 0;

  return(
    <FormWrapper onSubmit={handleSubmit}>
      <Error>{detailErrorMessage}</Error>
      <Content show={toggle.show} isAdmin={isAdmin}>
        {isAdmin && (
          <FieldWrapper>
            <Field
              name="userId"
              label="Operador"
              data-test="reportSelectOperator"
              component={RenderSelect}
              options={operators}
            />
          </FieldWrapper>
        )}
        <FieldDate keepProps>
          <Field
            name="dateStart"
            label="Fecha de Inicio"
            data-test="reportSelectDateStart"
            type="text"
            portionWidth={1/2}
            minDate={dateCreated}
            onChange={date => handleChangeDatePicker(date)('start')}
            errorMessage={startDateErrorMessage}
            component={RenderDatePicker}
          />
          <Field
            name="dateEnd"
            label="Fecha de Fin"
            data-test="reportSelectDateEnd"
            type="text"
            portionWidth={1/2}
            minDate={dateCreated}
            onChange={date => handleChangeDatePicker(date)('end')}
            errorMessage={endDateErrorMessage}
            component={RenderDatePicker}
          />
        </FieldDate>
        <FieldWrapper>
          <Field
            name="stateId"
            label="Estado"
            data-test="reportSelectState"
            component={RenderSelect}
            options={CIP_STATES}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="cip"
            type="number"
            label="N° CIP"
            data-test="reportInputCipNumber"
            component={RenderInput}
            maxLength="14"
            onChange={handleChangeInput}
            disabled={lockCip}
            validate={[minLength8]}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="documentNumber"
            type="text"
            label="Nº Documento"
            data-test="reportInputDocumentNumber"
            component={RenderInput}
            maxLength="15"
            onChange={handleChangeInput}
            disabled={lockDocument}
            validate={[minLength8]}
            normalize={normalizeAlphanumeric}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="paymentType"
            label="Tipo de Pago"
            data-test="reportSelectPaymentType"
            component={RenderSelect}
            options={paymentTypes}
          />
        </FieldWrapper>
        {sessionRol &&
          <div>
            modal error
            <ModalContainer data-test="modal" reset={reset} message={message} sessionRol={sessionRol} />
          </div>
        }
      </Content>
      <Footer>
        <More data-test="reportSpanMore" onClick={toggle.onClick}>
          { toggle.show
            ? 'Ver menos filtros'
            : 'Ver más filtros' }
        </More>
        <Button primary data-test="reportButtonSearch" width="140px" type="submit" disabled={isFetchingDetails}>Buscar</Button>
        <TooltipWrapper>
          <Tooltip title="Descargar Excel">
            <Button
              type="button"
              data-test="reportButtonExcelTop"
              icon="download-cloud"
              width="94px"
              onClick={handleClickDownload}
              disabled={isDisabledButton}
            />
          </Tooltip>
        </TooltipWrapper>
      </Footer>
      {showModal &&
        <ModalContainer data-test="modal" openModal={showModal} message='Por favor, vuelva a iniciar sesión' reset={reset} />
      }
    </FormWrapper>
  );
};
